import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Shield, Check, Loader2, AlertTriangle, Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";

export default function SignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [err, setErr] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    api.get(`/sign/${token}`).then(({ data }) => {
      setContract(data);
      if (data.client?.full_name) setTypedName(data.client.full_name);
    }).catch((e) => setErr(e?.response?.data?.detail || "Could not load contract"));
  }, [token]);

  // Canvas drawing
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const setSize = () => {
      const w = c.parentElement.clientWidth;
      c.width = w * window.devicePixelRatio;
      c.height = 160 * window.devicePixelRatio;
      c.style.width = w + "px";
      c.style.height = "160px";
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      ctx.strokeStyle = "#0A1930";
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };
    setSize();
    const getPt = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawingRef.current = true; const p = getPt(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const p = getPt(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; };
    c.addEventListener("mousedown", start);
    c.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", end);
    return () => {
      c.removeEventListener("mousedown", start);
      c.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start);
      c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", end);
    };
  }, [contract]);

  const clearSig = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
  };

  const submit = async () => {
    if (!consent) return;
    setSubmitting(true);
    try {
      const c = canvasRef.current;
      const dataUrl = c ? c.toDataURL("image/png") : null;
      const { data } = await api.post(`/sign/${token}`, {
        typed_name: typedName, consent: true, signature_data_url: dataUrl,
      });
      setSigned(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to sign.");
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-white grid place-items-center px-6">
      <div className="max-w-md text-center" data-testid="sign-error">
        <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
        <p className="mt-4 text-[#0A1930] font-display text-xl">{err}</p>
        <p className="mt-2 text-sm text-slate-500">Please contact For Those Who Served for a new link.</p>
      </div>
    </div>
  );
  if (!contract) return <div className="min-h-screen bg-white grid place-items-center text-slate-500">Loading…</div>;

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  if (signed) return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#F4F6FB] grid place-items-center px-6">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full bg-white border border-slate-200 rounded-3xl p-10 text-center"
        data-testid="sign-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 grid place-items-center">
          <Check className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
        </div>
        <h1 className="font-display text-3xl text-[#0A1930] mt-6 tracking-tight">Signed and sealed.</h1>
        <p className="mt-3 text-slate-600">Thank you. A copy of your signed agreement is being emailed to you.</p>
        <p className="mt-2 text-xs text-slate-400">Certificate ID: {signed.certificate_id}</p>
        <a href={`${apiBase}${signed.pdf_url}`} target="_blank" rel="noreferrer"
          className="mt-8 inline-flex items-center gap-2 bg-[#0066CC] text-white rounded-full px-6 py-3 text-sm hover:bg-[#0055AA]"
          data-testid="sign-pdf-link">
          <Download className="w-4 h-4" /> Download signed PDF
        </a>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-24">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/logo.png" alt="For Those Who Served" className="h-9 w-auto" />
          <div>
            <p className="font-display text-[15px] text-[#0A1930] leading-none">{contract.company_name}</p>
            <p className="text-[11px] text-slate-500 mt-1 tracking-wide">Secure signing · ESIGN Act compliant</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6">
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <p className="text-[11px] tracking-[0.18em] uppercase text-slate-500">Service Agreement</p>
          <h1 className="font-display text-2xl text-[#0A1930] mt-1 tracking-tight">For {contract.client.full_name}</h1>
          <p className="mt-2 text-sm text-slate-500">
            You pay nothing upfront. If we help you receive back pay / retroactive benefits,
            our fee is <span className="text-[#0A1930] font-medium">{contract.fee_percentage}%</span> of those benefits only.
          </p>
        </div>

        <article className="mt-4 bg-white border border-slate-200 rounded-3xl p-6 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="contract-body">
          {contract.body}
        </article>

        <div className="mt-4 bg-white border border-slate-200 rounded-3xl p-6">
          <h2 className="font-display text-lg text-[#0A1930]">Sign here</h2>
          <p className="text-xs text-slate-500 mt-1">Draw your signature, then type your full legal name to confirm.</p>

          <div className="mt-4 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden">
            <canvas ref={canvasRef} className="block w-full touch-none" data-testid="signature-pad" />
          </div>
          <div className="mt-2 flex justify-between">
            <button onClick={clearSig} className="text-xs text-slate-500 hover:text-slate-700" data-testid="sig-clear">Clear</button>
            <span className="text-[11px] text-slate-400">Use finger on mobile</span>
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-slate-500">Typed full legal name</label>
              <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} className="h-12 mt-1" data-testid="sig-typed-name" />
            </div>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="sig-consent" />
              <span>
                I agree to use electronic records and signatures under the U.S. ESIGN Act,
                and I consent to the terms of this Service Agreement.
              </span>
            </label>
          </div>

          <Button onClick={submit} disabled={!consent || !typedName.trim() || submitting}
            className="mt-6 w-full h-12 rounded-full bg-[#0066CC] hover:bg-[#0055AA]"
            data-testid="sig-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing…</> : <><Shield className="w-4 h-4 mr-2" /> Sign agreement</>}
          </Button>

          <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
            For your security and audit, we record the IP, device, and timestamp at the moment of signing.
            A tamper-evident PDF and audit certificate will be emailed to you immediately.
          </p>
        </div>
      </main>
    </div>
  );
}
